import { useAuth, useOrganizationList } from '@clerk/react';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useSWRConfig } from 'swr';
import {
  armCatalogLiveWait,
  catalogLiveTier,
  catalogReadsPassedGate,
  catalogSwrKeyFollowsTier,
  entitlementShouldRevalidate,
  forgetCatalogLiveEntitlement,
  getCatalogLiveSnapshot,
  setCatalogLiveEnabled,
  setCatalogLiveSession,
  subscribeCatalogLive,
  type CatalogLiveSnapshot,
} from '../data/catalogAuth';

const INDEXER_BASE: string = import.meta.env.VITE_INDEXER_BASE ?? '';

type UsagePlan = { plan?: string };

function applyEntitlement(
  next: { entitled: boolean; getToken: (() => Promise<string | null>) | null },
  mutate: (filter: (key: unknown) => boolean) => Promise<unknown>
): void {
  const wasLive = catalogLiveTier() === 'live';
  // Capture before release. Waiters resume in a microtask and would otherwise
  // look like reads that already finished on the old tier.
  const readsPassedGate = catalogReadsPassedGate();
  setCatalogLiveSession(next);
  if (entitlementShouldRevalidate(wasLive, catalogLiveTier() === 'live', readsPassedGate)) {
    void mutate(catalogSwrKeyFollowsTier);
  }
}

/**
 * A signed-in session can render before its only membership is active.
 * `useEnsureActiveOrg` calls `setActive` once that list resolves, so a missing
 * org id is not yet "not entitled" while that activation is still in flight.
 */
function orgActivationPending(
  orgsLoaded: boolean,
  membershipsLoading: boolean,
  memberships: readonly { organization: { id: string } }[] | undefined,
  orgId: string | null | undefined
): boolean {
  if (orgId) return false;
  if (!orgsLoaded || membershipsLoading || memberships == null) return true;
  if (memberships.length !== 1) return false;
  const onlyOrgId = memberships[0]?.organization.id;
  return onlyOrgId != null && onlyOrgId !== orgId;
}

/**
 * Arms the live-catalog gate and clears entitlement when the visitor is signed
 * out. Signed-in billing is `useCatalogLiveSignedIn`, mounted only after Clerk
 * has a user, so `useOrganizationList` stays off signed-out sessions. Embeds
 * never mount this hook (no ClerkProvider). The footer switch owns the
 * preference.
 */
export function useCatalogLiveAuth(): void {
  const { isLoaded, isSignedIn } = useAuth();
  // Hold through Clerk load and a signed-in session. A signed-out render
  // must not arm a wait that billing will never release.
  if (!isLoaded || isSignedIn) armCatalogLiveWait();
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    const wasLive = catalogLiveTier() === 'live';
    forgetCatalogLiveEntitlement();
    if (wasLive) void mutate(() => true);
  }, [isLoaded, isSignedIn, mutate]);
}

/** Applies Pro/Premium entitlement once the signed-in org is settled. */
export function useCatalogLiveSignedIn(): void {
  const { orgId, getToken } = useAuth();
  const { isLoaded: orgsLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const { mutate } = useSWRConfig();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const activationPending = orgActivationPending(
    orgsLoaded,
    userMemberships.isLoading,
    userMemberships.data,
    orgId
  );

  useEffect(() => {
    if (activationPending) return;
    if (!orgId) {
      applyEntitlement({ entitled: false, getToken: null }, mutate);
      return;
    }
    let cancelled = false;
    const readPlan = async (): Promise<void> => {
      try {
        const token = await getTokenRef.current({ template: 'indexer' });
        if (!token || cancelled) {
          if (!cancelled) applyEntitlement({ entitled: false, getToken: null }, mutate);
          return;
        }
        const res = await fetch(`${INDEXER_BASE}/api/v1/billing/usage`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) {
          if (!cancelled) applyEntitlement({ entitled: false, getToken: null }, mutate);
          return;
        }
        const usage = (await res.json()) as UsagePlan;
        const entitled = usage.plan === 'pro' || usage.plan === 'premium';
        if (cancelled) return;
        applyEntitlement(
          {
            entitled,
            getToken: entitled ? () => getTokenRef.current({ template: 'indexer' }) : null,
          },
          mutate
        );
      } catch {
        if (!cancelled) applyEntitlement({ entitled: false, getToken: null }, mutate);
      }
    };
    void readPlan();
    return () => {
      cancelled = true;
    };
  }, [activationPending, orgId, mutate]);
}

export function useCatalogLive(): CatalogLiveSnapshot {
  return useSyncExternalStore(subscribeCatalogLive, getCatalogLiveSnapshot, getCatalogLiveSnapshot);
}

export function useSetCatalogLiveEnabled(): (enabled: boolean) => void {
  const { mutate } = useSWRConfig();
  return (enabled: boolean) => {
    const wasLive = catalogLiveTier() === 'live';
    setCatalogLiveEnabled(enabled);
    if (catalogLiveTier() === 'live' || wasLive) void mutate(catalogSwrKeyFollowsTier);
  };
}
